import { Fragment, memo, useCallback, useMemo } from 'react';
import {
  DialogContent,
  DialogHeader,
  DialogTitle,
  Dialog,
  DialogDescription,
} from '@antimatterhq/ui/components/dialog';
import {
  SimpleTable,
  SimpleTableBody,
  SimpleTableCell,
  SimpleTableHead,
  SimpleTableRow,
} from '@antimatterhq/ui/components/simpleTable';
import { Button } from '@antimatterhq/ui/components/button';
import { useNavigate } from 'react-router-dom';
import { Text } from '@antimatterhq/ui/components/typography';
import { Checkbox } from '@antimatterhq/ui/components/checkbox';
import { useDatasourceUpgrade } from '@/contexts/DatasourceUpgradeContext';
import { getUpgradeChangeKey } from '@/lib/datasource/upgrade/utils/datasourceUpgradeUtils';
import { queryBronzeChanges } from '@/lib/datasource/upgrade/bronze/datasourceUpgradeBronzeHelper';
import { queryGoldChanges } from '@/lib/datasource/upgrade/gold/datasourceUpgradeGoldHelper';
import { queryPreTransformChanges } from '@/lib/datasource/upgrade/preTransform/datasourceUpgradePreTransformHelper';
import { DatasourceUpgradeItemDiff } from '@/lib/datasource/upgrade/datasourceUpgrade.types';
import { querySilverChanges } from '@/lib/datasource/upgrade/silver/datasourceUpgradeSilverHelper';

export type DatasourceUpgradeOverviewDialogProps = {
  isOpen: boolean;
  onClose: () => void;
};

const ChangeAction = memo(
  ({
    change,
    disabled,
  }: {
    change: DatasourceUpgradeItemDiff;
    disabled: boolean;
  }) => {
    const { setFieldStatus } = useDatasourceUpgrade();

    const onRevert = useCallback(() => {
      setFieldStatus(change, 'unapplied');
    }, [change, setFieldStatus]);

    const onConfirm = useCallback(() => {
      setFieldStatus(change, 'confirmed');
    }, [change, setFieldStatus]);

    return (
      <Checkbox
        checked={change.status === 'confirmed'}
        onCheckedChange={(checked) => (checked ? onConfirm() : onRevert())}
        data-testid="datasource-upgrade-overview-checkbox"
        disabled={disabled || change.status === 'conflicted'}
      />
    );
  },
);

ChangeAction.displayName = 'ChangeAction';

const ChangeStatus = memo(
  ({ change }: { change: DatasourceUpgradeItemDiff }) => {
    return <>{change.status === 'conflicted' ? 'Yes' : 'No'}</>;
  },
);

ChangeStatus.displayName = 'ChangeStatus';

const ChangeDescription = memo(
  ({ change }: { change: DatasourceUpgradeItemDiff }) => {
    return <>{change.description}</>;
  },
);

ChangeDescription.displayName = 'ChangeDescription';

const Row = memo(
  ({
    change,
    stage,
    tableName,
    disabled,
    showStatus,
  }: {
    change: DatasourceUpgradeItemDiff;
    stage: string;
    tableName?: string;
    disabled: boolean;
    showStatus?: boolean;
  }) => {
    return (
      <SimpleTableRow key={getUpgradeChangeKey(change)} hover={false}>
        <SimpleTableCell>{stage}</SimpleTableCell>
        <SimpleTableCell>{tableName}</SimpleTableCell>
        <SimpleTableCell>
          <ChangeDescription change={change} />
        </SimpleTableCell>
        {showStatus && (
          <SimpleTableCell>
            <ChangeStatus change={change} />
          </SimpleTableCell>
        )}
        <SimpleTableCell>
          <div className="flex justify-end">
            <ChangeAction change={change} disabled={disabled} />
          </div>
        </SimpleTableCell>
      </SimpleTableRow>
    );
  },
);

Row.displayName = 'Row';

export const DatasourceUpgradeOverviewDialog = ({
  isOpen,
  onClose,
}: DatasourceUpgradeOverviewDialogProps) => {
  const {
    bronze,
    preTransform,
    silver,
    gold,
    setFieldStatus,
    datasource,
    presetUpgradeVersion,
    hasConflictedChanges,
    hasUnconfirmedChanges,
    publish,
    title,
    description,
    isSaving,
    isPublishing,
    save,
    diffs,
  } = useDatasourceUpgrade();
  const navigate = useNavigate();

  const bronzeUnappliedChanges = useMemo(() => {
    return queryBronzeChanges(bronze, { status: 'unapplied' });
  }, [bronze]);

  const bronzeConfirmedChanges = useMemo(() => {
    return queryBronzeChanges(bronze, { status: 'confirmed' });
  }, [bronze]);

  const preTransformUnappliedChanges = useMemo(() => {
    return queryPreTransformChanges(preTransform, { status: 'unapplied' });
  }, [preTransform]);

  const preTransformConfirmedChanges = useMemo(() => {
    return queryPreTransformChanges(preTransform, { status: 'confirmed' });
  }, [preTransform]);

  const silverUnappliedChanges = useMemo(() => {
    return querySilverChanges(silver, { status: 'unapplied' });
  }, [silver]);

  const silverConfirmedChanges = useMemo(() => {
    return querySilverChanges(silver, { status: 'confirmed' });
  }, [silver]);

  const goldUnappliedChanges = useMemo(() => {
    return queryGoldChanges(gold, { status: 'unapplied' });
  }, [gold]);

  const goldConfirmedChanges = useMemo(() => {
    return queryGoldChanges(gold, { status: 'confirmed' });
  }, [gold]);

  const acceptAllState = useMemo(() => {
    // for the purposes of checking the accept all state, we ignore conflicted changes
    const filteredDiffs = diffs.filter((diff) => diff.status !== 'conflicted');

    // if every remaining change is confirmed, return true/checked
    if (filteredDiffs.every((diff) => diff.status === 'confirmed')) {
      return true;
    }

    // if some remaining change is confirmed, return indeterminate/mixed
    if (filteredDiffs.some((diff) => diff.status === 'confirmed')) {
      return 'indeterminate';
    }

    // if no remaining change is confirmed, return false/unchecked
    return false;
  }, [diffs]);

  const acceptAll = useCallback(() => {
    setFieldStatus(
      [
        ...bronzeUnappliedChanges,
        ...silverUnappliedChanges,
        ...goldUnappliedChanges,
        ...preTransformUnappliedChanges,
      ],
      'confirmed',
    );
  }, [
    bronzeUnappliedChanges,
    silverUnappliedChanges,
    goldUnappliedChanges,
    preTransformUnappliedChanges,
    setFieldStatus,
  ]);

  const unacceptAll = useCallback(() => {
    setFieldStatus(
      [
        ...bronzeConfirmedChanges,
        ...silverConfirmedChanges,
        ...goldConfirmedChanges,
        ...preTransformConfirmedChanges,
      ],
      'unapplied',
    );
  }, [
    bronzeConfirmedChanges,
    silverConfirmedChanges,
    goldConfirmedChanges,
    preTransformConfirmedChanges,
    setFieldStatus,
  ]);

  const handleAcceptAll = useCallback(() => {
    if (acceptAllState === true) {
      unacceptAll();
    } else {
      acceptAll();
    }
  }, [acceptAllState, acceptAll, unacceptAll]);

  const handleReviewChanges = useCallback(() => {
    save({
      onSuccess: async () => {
        onClose();
        navigate(
          `/configure/data-sources/upgrade/${datasource.metadata?.name}?upgradePresetVersion=${presetUpgradeVersion}`,
        );
      },
    });
  }, [onClose, navigate, datasource, presetUpgradeVersion, save]);

  const handleApplyChanges = useCallback(() => {
    publish({
      onSuccess: () => {
        onClose();
      },
    });
  }, [onClose, publish]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-h-[90vh] max-w-[90vw]"
        data-testid="datasource-upgrade-overview-content"
      >
        <DialogHeader>
          <DialogTitle data-testid="datasource-upgrade-overview-title">
            {title}
          </DialogTitle>
        </DialogHeader>
        <DialogDescription data-testid="datasource-upgrade-overview-description">
          <Text bold className="block">
            What&apos;s new in this release?
          </Text>
          {description}
        </DialogDescription>
        <SimpleTable>
          <SimpleTableBody>
            <SimpleTableRow hover={false}>
              <SimpleTableHead className="font-bold">Stage</SimpleTableHead>
              <SimpleTableHead className="font-bold">Table</SimpleTableHead>
              <SimpleTableHead className="font-bold">Change</SimpleTableHead>
              {hasConflictedChanges && (
                <SimpleTableHead className="font-bold">
                  Do you have edits in this field?
                </SimpleTableHead>
              )}
              <SimpleTableHead className="text-right">
                Accept changes
              </SimpleTableHead>
            </SimpleTableRow>
            {bronze.changes.map((change) => (
              <Row
                key={getUpgradeChangeKey(change)}
                change={change}
                stage="Bronze"
                tableName={datasource.spec?.bronze?.bronzeTable || ''}
                disabled={isSaving}
                showStatus={hasConflictedChanges}
              />
            ))}
            {preTransform.changes.map((change) => (
              <Row
                key={getUpgradeChangeKey(change)}
                change={change}
                stage="Pre-Transform"
                disabled={isSaving}
                showStatus={hasConflictedChanges}
              />
            ))}
            {silver.tables.map((table) => (
              <Fragment key={table.tableName}>
                {table.changes.map((change) => (
                  <Row
                    key={getUpgradeChangeKey(change)}
                    change={change}
                    stage="Silver"
                    tableName={table.tableName}
                    disabled={isSaving}
                    showStatus={hasConflictedChanges}
                  />
                ))}
              </Fragment>
            ))}
            {gold.tables.map((table) => (
              <Fragment key={table.tableName}>
                {table.changes.map((change) => (
                  <Row
                    key={getUpgradeChangeKey(change)}
                    change={change}
                    stage="Gold"
                    tableName={table.tableName}
                    disabled={isSaving}
                    showStatus={hasConflictedChanges}
                  />
                ))}
              </Fragment>
            ))}
          </SimpleTableBody>
        </SimpleTable>

        <div className="-mt-2 flex justify-end">
          <div className="flex items-center gap-2">
            <Text>
              {' '}
              Accept{' '}
              {hasConflictedChanges && (
                <>{hasConflictedChanges && 'non-conflicting'} changes</>
              )}
              {!hasConflictedChanges && <>all</>}
            </Text>
            <Checkbox
              checked={acceptAllState}
              onCheckedChange={handleAcceptAll}
              disabled={isSaving}
              data-testid="datasource-upgrade-overview-accept-all-checkbox"
            />
          </div>
        </div>

        <div className="flex justify-between">
          <div>
            <Button
              variant="default"
              onClick={onClose}
              disabled={isSaving || isPublishing}
              data-testid="datasource-upgrade-overview-cancel-btn"
            >
              Cancel
            </Button>
          </div>
          <div className="flex justify-end gap-2">
            {hasConflictedChanges && hasUnconfirmedChanges && (
              <Button
                variant="primary"
                onClick={handleReviewChanges}
                disabled={isSaving || isPublishing}
                loading={isSaving}
                data-testid="datasource-upgrade-overview-review-remaining-btn"
              >
                Review remaining changes
              </Button>
            )}
            {!hasConflictedChanges && (
              <Button
                variant="default"
                onClick={handleReviewChanges}
                disabled={isSaving || isPublishing}
                loading={isSaving}
                data-testid="datasource-upgrade-overview-review-btn"
              >
                Review changes
              </Button>
            )}
            {!hasConflictedChanges && (
              <Button
                variant="primary"
                onClick={handleApplyChanges}
                disabled={hasUnconfirmedChanges || isSaving || isPublishing}
                loading={isPublishing}
                data-testid="datasource-upgrade-overview-apply-btn"
              >
                Apply changes
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
